// Vendors / Hosts — backed by /admin/vendors.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { E164_RE, normalizeE164Input } from '@drivecars/shared';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  DataTable,
  type DataTableColumn,
  type RowKey,
} from '@drivecars/admin-ui';
import {
  Avatar,
  PageHeader,
  ToolBtn,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { env } from '@/lib/env';
import { ApiError } from '@/lib/api';
import {
  activateVendor,
  approveVendorKyc,
  bulkVendorAction,
  createVendor,
  downloadVendorsCsv,
  fetchAdminVendors,
  inviteVendor,
  rejectVendorKyc,
  suspendVendor,
  unsuspendVendor,
  type AdminVendorRow,
  type AdminVendorsListParams,
  type CreateVendorResult,
  type VendorBulkAction,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';
import { fixedPriceBadge } from './vendor-fixed-price-state';

function resolveAvatarUrl(rel: string | null | undefined): string | null {
  if (!rel) return null;
  if (/^https?:\/\//.test(rel)) return rel;
  return `${env().PAYLOAD_URL}${rel}`;
}

type FilterKey =
  | 'all'
  | NonNullable<AdminVendorsListParams['verification']>
  | 'suspended';

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'approved', label: 'Verified' },
  { key: 'pending', label: 'KYC pending' },
  { key: 'rejected', label: 'KYC rejected' },
  { key: 'unverified', label: 'Unverified' },
  { key: 'suspended', label: 'Suspended' },
];

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v);

interface ActionCtx {
  busy: number | null;
  onApproveKyc: (r: AdminVendorRow) => void;
  onRejectKyc: (r: AdminVendorRow) => void;
  onSuspend: (r: AdminVendorRow) => void;
  onUnsuspend: (r: AdminVendorRow) => void;
  onActivate: (r: AdminVendorRow) => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminVendorRow>[] => [
  {
    key: 'name',
    h: 'Crew',
    cell: (r) => (
      <div className="flex items-center gap-3">
        <Avatar name={r.displayName} size={28} src={resolveAvatarUrl(r.avatarUrl)} />
        <div className="min-w-0">
          <span className="truncate font-medium">{r.displayName}</span>
          <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
            {r.email} · {r.planName ?? 'no plan'}
          </div>
        </div>
      </div>
    ),
  },
  { key: 'businessAddress', h: 'Address', cell: (r) => r.businessAddress ?? '—' },
  {
    key: 'cars',
    h: 'Cars',
    right: true,
    cell: (r) => <span className="tabular-nums">{r.carsTotal}</span>,
  },
  {
    key: 'bookings',
    h: 'Bookings',
    right: true,
    cell: (r) => <span className="tabular-nums">{r.bookingsTotal}</span>,
  },
  {
    key: 'commission',
    h: 'Commission',
    right: true,
    cell: (r) => (
      <span className="tabular-nums">
        {r.commissionValue == null
          ? '—'
          : r.commissionType === 'percent'
            ? `${r.commissionValue}%`
            : fmtMoney(r.commissionValue)}
      </span>
    ),
  },
  {
    key: 'status',
    h: 'Status',
    cell: (r) => <StatusPill status={r.status} />,
  },
  {
    key: 'verification',
    h: 'KYC',
    cell: (r) => <StatusPill status={r.verificationStatus ?? 'unverified'} />,
  },
  {
    key: 'fixedPrice',
    h: 'Fixed price',
    cell: (r) => {
      const badge = fixedPriceBadge(r);
      return badge ? <StatusPill status={badge} /> : null;
    },
  },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busy === r.id;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.verificationStatus !== 'approved' ? (
            <RowAction kind="primary" busy={busy} onClick={() => ctx.onApproveKyc(r)}>
              Approve KYC
            </RowAction>
          ) : null}
          {r.verificationStatus !== 'rejected' ? (
            <RowAction kind="danger" busy={busy} onClick={() => ctx.onRejectKyc(r)}>
              Reject
            </RowAction>
          ) : null}
          {r.status === 'pending' ? (
            <RowAction kind="primary" busy={busy} onClick={() => ctx.onActivate(r)}>
              Activate
            </RowAction>
          ) : null}
          {r.status === 'suspended' ? (
            <RowAction busy={busy} onClick={() => ctx.onUnsuspend(r)}>
              Unsuspend
            </RowAction>
          ) : (
            <RowAction kind="danger" busy={busy} onClick={() => ctx.onSuspend(r)}>
              Suspend
            </RowAction>
          )}
        </div>
      );
    },
  },
];

export function Vendors() {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<RowKey>>(new Set());
  const [bulkResult, setBulkResult] = useState<{
    succeeded: number;
    failed: number;
  } | null>(null);

  const params: AdminVendorsListParams = (() => {
    if (filter === 'all') return { q: q.trim() || undefined, page, limit };
    if (filter === 'suspended') {
      return { status: 'suspended', q: q.trim() || undefined, page, limit };
    }
    return { verification: filter, q: q.trim() || undefined, page, limit };
  })();

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadVendorsCsv({
        status: filter === 'suspended' ? 'suspended' : undefined,
        verification:
          filter === 'suspended' || filter === 'all' ? undefined : filter,
        q: q.trim() || undefined,
      });
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'vendors', params],
    queryFn: ({ signal }) => fetchAdminVendors(params, signal),
    placeholderData: keepPreviousData,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'vendors'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
  };

  const approveKycMut = useMutation({ mutationFn: approveVendorKyc, onSuccess: invalidate });
  const rejectKycMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => rejectVendorKyc(id, reason),
    onSuccess: invalidate,
  });
  const suspendMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => suspendVendor(id, reason),
    onSuccess: invalidate,
  });
  const unsuspendMut = useMutation({ mutationFn: unsuspendVendor, onSuccess: invalidate });
  const activateMut = useMutation({ mutationFn: activateVendor, onSuccess: invalidate });
  const bulkMut = useMutation({
    mutationFn: ({
      ids,
      action,
      reason,
    }: {
      ids: number[];
      action: VendorBulkAction;
      reason?: string;
    }) => bulkVendorAction(ids, action, reason),
    onSuccess: (res) => {
      setBulkResult({ succeeded: res.succeeded.length, failed: res.failed.length });
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const runBulk = (action: VendorBulkAction) => {
    const ids = Array.from(selectedIds, Number).filter(Number.isFinite);
    if (ids.length === 0) return;
    const verb =
      action === 'kyc-approve'
        ? 'Approve KYC for'
        : action === 'kyc-reject'
          ? 'Reject KYC for'
          : action === 'suspend'
            ? 'Suspend'
            : 'Unsuspend';
    if (!window.confirm(`${verb} ${ids.length} vendor${ids.length === 1 ? '' : 's'}?`)) {
      return;
    }
    let reason: string | undefined;
    if (action === 'kyc-reject' || action === 'suspend') {
      reason = window.prompt('Reason (optional):') ?? undefined;
    }
    bulkMut.mutate({ ids, action, reason });
  };

  const busyId =
    (approveKycMut.isPending && (approveKycMut.variables as number)) ||
    (rejectKycMut.isPending && (rejectKycMut.variables as { id: number }).id) ||
    (suspendMut.isPending && (suspendMut.variables as { id: number }).id) ||
    (unsuspendMut.isPending && (unsuspendMut.variables as number)) ||
    (activateMut.isPending && (activateMut.variables as number)) ||
    null;

  const cols = buildColumns({
    busy: busyId,
    onApproveKyc: (r) => approveKycMut.mutate(r.id),
    onRejectKyc: (r) => {
      const reason = window.prompt('Rejection reason (optional):') ?? undefined;
      rejectKycMut.mutate({ id: r.id, reason });
    },
    onSuspend: (r) => {
      const reason = window.prompt('Suspension reason (optional):') ?? undefined;
      suspendMut.mutate({ id: r.id, reason });
    },
    onUnsuspend: (r) => unsuspendMut.mutate(r.id),
    onActivate: (r) => activateMut.mutate(r.id),
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreateVendorResult | null>(null);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const mutationError =
    approveKycMut.error || rejectKycMut.error || suspendMut.error || unsuspendMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Operate"
        title="Crew"
        sub={`${total.toLocaleString()} crew${filter === 'all' ? '' : ` · ${filter}`}.`}
        actions={
          <>
            <ToolBtn icon="download" onClick={() => void handleExport()}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </ToolBtn>
            <ToolBtn icon="plus" onClick={() => setInviteOpen(true)}>
              Invite to crew
            </ToolBtn>
            <ToolBtn icon="plus" primary onClick={() => setCreateOpen(true)}>
              Add crew
            </ToolBtn>
          </>
        }
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn't load vendors: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}
      {exportError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Export failed: {exportError}
        </div>
      ) : null}
      {inviteSentTo ? (
        <div
          role="status"
          className="flex items-center justify-between rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] px-3 py-2 text-[12.5px] text-[#065f46]"
        >
          <span>
            Invite sent to <strong>{inviteSentTo}</strong>. They'll get an email to set their
            password and finish onboarding.
          </span>
          <button
            type="button"
            onClick={() => setInviteSentTo(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      {created ? (
        <div
          role="status"
          className="flex items-center justify-between rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] px-3 py-2 text-[12.5px] text-[#065f46]"
        >
          <span>
            Crew <strong>{created.email}</strong> created ({created.status}).{' '}
            {created.loginReady
              ? 'They can sign in with the password you set.'
              : 'They got an email to set their password — everything else works meanwhile.'}
          </span>
          <button
            type="button"
            onClick={() => setCreated(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search name, email, business…" />
        {FILTERS.map((f) => (
          <ToolChip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      {bulkResult ? (
        <div
          role="status"
          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-[12.5px] ${
            bulkResult.failed === 0
              ? 'border-[#a7f3d0] bg-[#ecfdf5] text-[#065f46]'
              : 'border-[#fde68a] bg-[#fef9c3] text-[#854d0e]'
          }`}
        >
          <span>
            Bulk action complete: {bulkResult.succeeded} succeeded
            {bulkResult.failed > 0 ? `, ${bulkResult.failed} failed` : ''}.
          </span>
          <button
            type="button"
            onClick={() => setBulkResult(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      {selectedIds.size > 0 ? (
        <div className="sticky top-[60px] z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 shadow-sm">
          <div className="text-[12.5px]">
            <strong>{selectedIds.size}</strong> selected
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ms-3 text-[11.5px] text-[color:var(--bo-mute)] hover:underline"
            >
              clear
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <RowAction
              kind="primary"
              busy={bulkMut.isPending}
              onClick={() => runBulk('kyc-approve')}
            >
              Approve KYC
            </RowAction>
            <RowAction
              busy={bulkMut.isPending}
              onClick={() => runBulk('kyc-reject')}
            >
              Reject KYC
            </RowAction>
            <RowAction
              busy={bulkMut.isPending}
              onClick={() => runBulk('unsuspend')}
            >
              Reinstate
            </RowAction>
            <RowAction
              kind="danger"
              busy={bulkMut.isPending}
              onClick={() => runBulk('suspend')}
            >
              Suspend
            </RowAction>
          </div>
        </div>
      ) : null}
      <DataTable
        columns={cols}
        rows={rows}
        onRowClick={(r) => void navigate({ to: `/crew/${r.id}` as never })}
        emptyLabel={isLoading ? 'Loading…' : 'No matching vendors.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
        selection={{
          selectedIds,
          onToggle: (id) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            });
          },
          onToggleAll: (ids) => {
            setSelectedIds((prev) => {
              const allHere = ids.every((id) => prev.has(id));
              const next = new Set(prev);
              if (allHere) {
                for (const id of ids) next.delete(id);
              } else {
                for (const id of ids) next.add(id);
              }
              return next;
            });
          },
        }}
      />
      {inviteOpen ? (
        <InviteVendorModal
          onClose={() => setInviteOpen(false)}
          onSent={(email) => {
            setInviteOpen(false);
            setInviteSentTo(email);
            invalidate();
          }}
        />
      ) : null}
      {createOpen ? (
        <CreateCrewModal
          onClose={() => setCreateOpen(false)}
          onCreated={(res) => {
            setCreateOpen(false);
            setCreated(res);
            invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

function InviteVendorModal({
  onClose,
  onSent,
}: {
  onClose: () => void;
  onSent: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      inviteVendor({
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        businessName: businessName.trim() || undefined,
        whatsappPhone: normalizeE164Input(whatsappPhone),
      }),
    onSuccess: (res) => onSent(res.email),
  });

  const errorMessage = (() => {
    const err = mut.error;
    if (!err) return null;
    if (err instanceof ApiError) {
      const code =
        err.body && typeof err.body === 'object' && 'error' in err.body
          ? String((err.body as { error?: unknown }).error)
          : null;
      if (code === 'email_taken') return 'Someone with that email is already in the crew.';
      if (code === 'invalid_payload')
        return 'Please check the email address and WhatsApp number.';
    }
    return 'Could not send the invite. Please try again.';
  })();

  // WhatsApp must be E.164 (+ then 8-15 digits) — it feeds bid broadcasts
  // and booking notifications, so a loosely-formatted number is useless.
  const whatsappOk = E164_RE.test(normalizeE164Input(whatsappPhone));
  const canSubmit = /\S+@\S+\.\S+/.test(email.trim()) && whatsappOk && !mut.isPending;

  const field =
    'h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-mute)]';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Invite to crew"
      onClick={onClose}
    >
      <form
        className="w-full max-w-md space-y-3 rounded-xl border border-[color:var(--bo-line)] bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mut.mutate();
        }}
      >
        <div>
          <h2 className="text-[15px] font-semibold text-[color:var(--bo-ink)]">Invite to crew</h2>
          <p className="mt-0.5 text-[12px] text-[color:var(--bo-mute)]">
            We'll email them a link to set their password and finish onboarding.
          </p>
        </div>

        {errorMessage ? (
          <div className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
            {errorMessage}
          </div>
        ) : null}

        <label className="block space-y-1">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Email *</span>
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vendor@example.com"
            className={field}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={field} />
          </label>
          <label className="block space-y-1">
            <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={field} />
          </label>
        </div>

        <label className="block space-y-1">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">
            WhatsApp number *
          </span>
          <input
            type="tel"
            required
            value={whatsappPhone}
            onChange={(e) => setWhatsappPhone(e.target.value)}
            placeholder="+212600000000"
            className={field}
          />
          <span className="block text-[11px] text-[color:var(--bo-mute)]">
            International format with country code, e.g. +212600000000.
          </span>
        </label>

        <label className="block space-y-1">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Business name</span>
          <input
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Optional"
            className={field}
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-9 rounded-lg bg-[color:var(--bo-ink)] px-3 text-[13px] font-medium text-white hover:bg-black disabled:opacity-50"
          >
            {mut.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Create crew directly (no signup round-trip) ─────────────────────
//
// Sibling of InviteVendorModal: same identity fields, plus an optional
// password (set → the crew can sign in immediately; empty → they get a
// set-password email) and a status choice. Defaults to `active` so the crew
// is instantly biddable + assignable in the new-listing host picker.

function CreateCrewModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (res: CreateVendorResult) => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'active' | 'pending'>('active');

  const mut = useMutation({
    mutationFn: () =>
      createVendor({
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        businessName: businessName.trim() || undefined,
        whatsappPhone: normalizeE164Input(whatsappPhone),
        password: password || undefined,
        status,
      }),
    onSuccess: (res) => onCreated(res),
  });

  const errorMessage = (() => {
    const err = mut.error;
    if (!err) return null;
    if (err instanceof ApiError) {
      const code =
        err.body && typeof err.body === 'object' && 'error' in err.body
          ? String((err.body as { error?: unknown }).error)
          : null;
      if (code === 'email_taken') return 'Someone with that email is already in the crew.';
      if (code === 'invalid_payload')
        return 'Please check the email address, WhatsApp number, and password (min 8 characters).';
    }
    return 'Could not create the crew. Please try again.';
  })();

  const whatsappOk = E164_RE.test(normalizeE164Input(whatsappPhone));
  const passwordOk = password === '' || password.length >= 8;
  const canSubmit =
    /\S+@\S+\.\S+/.test(email.trim()) && whatsappOk && passwordOk && !mut.isPending;

  const field =
    'h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-mute)]';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add crew"
      onClick={onClose}
    >
      <form
        className="w-full max-w-md space-y-3 rounded-xl border border-[color:var(--bo-line)] bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mut.mutate();
        }}
      >
        <div>
          <h2 className="text-[15px] font-semibold text-[color:var(--bo-ink)]">Add crew</h2>
          <p className="mt-0.5 text-[12px] text-[color:var(--bo-mute)]">
            Creates the account directly — no signup round-trip. Set a password for instant
            sign-in, or leave it empty to email them a set-password link.
          </p>
        </div>

        {errorMessage ? (
          <div className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
            {errorMessage}
          </div>
        ) : null}

        <label className="block space-y-1">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Email *</span>
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vendor@example.com"
            className={field}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={field} />
          </label>
          <label className="block space-y-1">
            <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={field} />
          </label>
        </div>

        <label className="block space-y-1">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">
            WhatsApp number *
          </span>
          <input
            type="tel"
            required
            value={whatsappPhone}
            onChange={(e) => setWhatsappPhone(e.target.value)}
            placeholder="+212600000000"
            className={field}
          />
          <span className="block text-[11px] text-[color:var(--bo-mute)]">
            International format with country code, e.g. +212600000000.
          </span>
        </label>

        <label className="block space-y-1">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Business name</span>
          <input
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Optional"
            className={field}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Optional — min 8 chars"
              minLength={8}
              className={field}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'active' | 'pending')}
              className={field}
            >
              <option value="active">Active (biddable now)</option>
              <option value="pending">Pending review</option>
            </select>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-9 rounded-lg bg-[color:var(--bo-ink)] px-3 text-[13px] font-medium text-white hover:bg-black disabled:opacity-50"
          >
            {mut.isPending ? 'Creating…' : 'Create crew'}
          </button>
        </div>
      </form>
    </div>
  );
}
